import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { integrationService } from "@/features/integration/services/integration.service";
import type { Integration } from "@/features/integration/interfaces";

const OAUTH_COMPANY_ID_KEY = "oauth_company_id";
const OAUTH_PLATFORM_KEY = "oauth_platform";
const TWITTER_OAUTH_TOKEN_KEY = "twitter_oauth_token";
const TOKEN_REQUEST_TIMEOUT_MS = 8000;

const ALLOWED_ORIGIN =
  typeof window !== "undefined" ? window.location.origin : "";

function sendToOpener(payload: {
  type: "oauth-complete";
  success: boolean;
  integration?: Integration;
  platform?: string;
  error?: string;
}) {
  if (typeof window !== "undefined" && window.opener && !window.opener.closed) {
    window.opener.postMessage(payload, ALLOWED_ORIGIN);
  }
}

function getCompanyId(searchParams: URLSearchParams): number | null {
  const fromUrl = searchParams.get("companyId");
  if (fromUrl) {
    const n = parseInt(fromUrl, 10);
    if (!Number.isNaN(n)) return n;
  }
  const fromStorage = sessionStorage.getItem(OAUTH_COMPANY_ID_KEY);
  if (fromStorage) {
    const n = parseInt(fromStorage, 10);
    if (!Number.isNaN(n)) return n;
  }
  return null;
}

function getPlatform(searchParams: URLSearchParams): string | null {
  const fromUrl = searchParams.get("platform")?.toLowerCase();
  if (fromUrl) return fromUrl;
  const fromStorage = sessionStorage.getItem(OAUTH_PLATFORM_KEY)?.toLowerCase();
  return fromStorage || null;
}

export const IntegrationsCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<
    "loading" | "auth-check" | "success" | "error" | "no-session"
  >("auth-check");
  const [message, setMessage] = useState<string>("Completing connection…");
  const tokenRequestTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flowStartedRef = useRef(false);

  const runCompleteFlow = useCallback(
    async (
      searchParams: URLSearchParams,
      overrides?: { companyId?: number; platform?: string }
    ) => {
      if (flowStartedRef.current) return;
      flowStartedRef.current = true;

      let closed = false;

      const cleanup = () => {
        closed = true;
        if (tokenRequestTimeoutRef.current) {
          clearTimeout(tokenRequestTimeoutRef.current);
          tokenRequestTimeoutRef.current = null;
        }
      };

      setStatus("loading");

      const errorParam = searchParams.get("error");
      if (errorParam) {
        const errMsg =
          errorParam === "access_denied"
            ? "You denied access to the app."
            : `Authorization failed: ${errorParam}`;
        setStatus("error");
        setMessage(errMsg);
        sendToOpener({
          type: "oauth-complete",
          success: false,
          error: errMsg,
        });
        return;
      }

      const platform =
        overrides?.platform?.toLowerCase() ?? getPlatform(searchParams);
      if (!platform) {
        setStatus("error");
        setMessage("Missing platform in callback.");
        sendToOpener({
          type: "oauth-complete",
          success: false,
          error: "Missing platform",
        });
        return;
      }

      const companyId =
        overrides?.companyId ?? getCompanyId(searchParams);
      if (!companyId) {
        setStatus("error");
        setMessage("Missing company. Please try again from the Platforms page.");
        sendToOpener({
          type: "oauth-complete",
          success: false,
          error: "Missing companyId",
        });
        return;
      }

      try {
        if (platform === "twitter") {
          const oauthToken =
            sessionStorage.getItem(TWITTER_OAUTH_TOKEN_KEY) ||
            searchParams.get("oauthToken") ||
            searchParams.get("oauth_token");
          const oauthVerifier =
            searchParams.get("oauthVerifier") ||
            searchParams.get("oauth_verifier");

          if (!oauthToken || !oauthVerifier) {
            setStatus("error");
            setMessage("Missing Twitter OAuth token or verifier.");
            sendToOpener({
              type: "oauth-complete",
              success: false,
              error: "Missing Twitter OAuth data",
            });
            return;
          }

          const integration = await integrationService.completeTwitter({
            oauthToken,
            oauthVerifier,
            companyId,
          });

          sessionStorage.removeItem(TWITTER_OAUTH_TOKEN_KEY);

          if (!closed) {
            setStatus("success");
            setMessage("Twitter connected successfully!");
            sendToOpener({
              type: "oauth-complete",
              success: true,
              integration,
              platform: "TWITTER",
            });
          }
        } else {
          const code = searchParams.get("code");
          if (!code) {
            setStatus("error");
            setMessage("Missing authorization code.");
            sendToOpener({
              type: "oauth-complete",
              success: false,
              error: "Missing code",
            });
            return;
          }

          const platformMap: Record<
            string,
            (params: { code: string; companyId: number }) => Promise<Integration>
          > = {
            instagram: (p) => integrationService.completeInstagram(p),
            facebook: (p) => integrationService.completeFacebook(p),
            tiktok: (p) => integrationService.completeTiktok(p),
            youtube: (p) => integrationService.completeYoutube(p),
            linkedin: (p) => integrationService.completeLinkedin(p),
          };

          const completeFn = platformMap[platform];
          if (!completeFn) {
            setStatus("error");
            setMessage(`Unknown platform: ${platform}`);
            sendToOpener({
              type: "oauth-complete",
              success: false,
              error: `Unknown platform: ${platform}`,
            });
            return;
          }

          const integration = await completeFn({ code, companyId });

          if (!closed) {
            setStatus("success");
            setMessage(`${platform.charAt(0).toUpperCase() + platform.slice(1)} connected successfully!`);
            sendToOpener({
              type: "oauth-complete",
              success: true,
              integration,
              platform: platform.toUpperCase(),
            });
          }
        }
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : "Connection failed.";
        if (!closed) {
          setStatus("error");
          setMessage(errorMessage);
          sendToOpener({
            type: "oauth-complete",
            success: false,
            error: errorMessage,
          });
        }
      } finally {
        sessionStorage.removeItem(OAUTH_COMPANY_ID_KEY);
        sessionStorage.removeItem(OAUTH_PLATFORM_KEY);
        if (!closed) {
          if (window.opener && !window.opener.closed) {
            setTimeout(() => window.close(), 1500);
          } else {
            window.location.href = "/home/integrations/platforms?connected=1";
          }
        }
      }

      return cleanup;
    },
    []
  );

  // 1) Try localStorage first; 2) If no token, ask parent via postMessage; 3) Timeout -> no-session (run only once)
  useEffect(() => {
    if (flowStartedRef.current) return;

    const hasToken =
      typeof window !== "undefined" &&
      !!window.localStorage?.getItem("accessToken");

    if (hasToken) {
      runCompleteFlow(searchParams);
      return;
    }

    // Popup may not share localStorage (e.g. Safari). Ask parent for session.
    if (!window.opener || window.opener.closed) {
      setStatus("no-session");
      setMessage(
        "Session not found. Please log in in the main window and try again from the Platforms page."
      );
      return;
    }

    const handleTokenFromParent = (event: MessageEvent) => {
      if (event.origin !== ALLOWED_ORIGIN || event.data?.type !== "oauth-callback-token") return;
      if (flowStartedRef.current) return;
      const { accessToken, refreshToken, companyId, platform } = event.data;
      if (typeof accessToken !== "string") return;
      window.removeEventListener("message", handleTokenFromParent);
      if (tokenRequestTimeoutRef.current) {
        clearTimeout(tokenRequestTimeoutRef.current);
        tokenRequestTimeoutRef.current = null;
      }
      try {
        window.localStorage.setItem("accessToken", accessToken);
        if (typeof refreshToken === "string") {
          window.localStorage.setItem("refreshToken", refreshToken);
        }
      } catch {
        setStatus("no-session");
        setMessage("Could not store session. Please try again.");
        return;
      }
      // Popup has its own sessionStorage; parent sends companyId + platform
      const overrides =
        typeof companyId === "number" && typeof platform === "string"
          ? { companyId, platform }
          : undefined;
      runCompleteFlow(searchParams, overrides);
    };

    window.addEventListener("message", handleTokenFromParent);
    window.opener.postMessage({ type: "oauth-callback-ready" }, ALLOWED_ORIGIN);
    setMessage("Getting session from main window…");

    tokenRequestTimeoutRef.current = setTimeout(() => {
      tokenRequestTimeoutRef.current = null;
      window.removeEventListener("message", handleTokenFromParent);
      setStatus("no-session");
      setMessage(
        "Session not found. Please log in in the main window and try again from the Platforms page."
      );
    }, TOKEN_REQUEST_TIMEOUT_MS);

    return () => {
      window.removeEventListener("message", handleTokenFromParent);
      if (tokenRequestTimeoutRef.current) {
        clearTimeout(tokenRequestTimeoutRef.current);
      }
    };
  }, [searchParams, runCompleteFlow]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-6">
      <div className="max-w-sm w-full text-center space-y-4">
        {(status === "auth-check" || status === "loading") && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
            <p className="text-foreground">
              {status === "auth-check" ? "Checking session…" : message}
            </p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto" />
            <p className="font-medium text-foreground">{message}</p>
            <p className="text-sm text-muted-foreground">
              This window will close automatically.
            </p>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="h-12 w-12 text-destructive mx-auto" />
            <p className="font-medium text-foreground">{message}</p>
            <p className="text-sm text-muted-foreground">
              You can close this window and try again from the Platforms page.
            </p>
          </>
        )}
        {status === "no-session" && (
          <>
            <XCircle className="h-12 w-12 text-destructive mx-auto" />
            <p className="font-medium text-foreground">{message}</p>
            <p className="text-sm text-muted-foreground">
              Close this window, log in in the main tab, then try connecting again.
            </p>
            <button
              type="button"
              onClick={() => window.close()}
              className="mt-4 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90"
            >
              Close window
            </button>
          </>
        )}
      </div>
    </div>
  );
};
